import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Container, DealerCard, Map, Row } from '@perxis-auto/ui-kit/lib';

import getDealerAddress from '@/features/dealers/helpers/getDealerAddress';
import getDealerPhones from '@/features/dealers/helpers/getDealerPhones';
import getDealerWorkTime from '@/features/dealers/helpers/getDealerWorkTime';
import useCityData from '@/features/dealers/hooks/useCityData';
import useDealerCenterData from '@/features/dealers/hooks/useDealerCenterData';
import { useAppSelector } from '@/features/hooks';
import { rootSelectors } from '@/features/store';
import useOccupiedAreas from '@/hooks/useOccupiedAreas';
import isDefined from '@/utils/isDefined';

import { DealerMapProps } from './DealerMap.types';

import styles from './DealerMap.module.scss';

function DealerMap(props: DealerMapProps) {
  const { t } = useTranslation();
  const { data: dealerCenterItem } = useDealerCenterData();

  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);

  const mapRef = useRef<HTMLDivElement | null>(null);

  const onBodyRefChange = useCallback(
    (node: HTMLDivElement | null) => setContainerRef(node),
    [],
  );

  const occupiedArea = useOccupiedAreas(containerRef);

  const { data: cityItem } = useCityData({
    collectionId: dealerCenterItem?.data?.address?.city?.collection_id,
    itemId: dealerCenterItem?.data?.address?.city?.id,
  });

  const mapsProvider = useAppSelector(rootSelectors.config.mapsProviderSelector);
  const mapsKey = useAppSelector(rootSelectors.config.mapsKeySelector);
  const placemarkImageDeprecated = useAppSelector(
    rootSelectors.config.mapsPlacemarkImageSelector,
  );
  const placemarkActiveImageDeprecated = useAppSelector(
    rootSelectors.config.mapsPlacemarkActiveImageSelector,
  );
  const placemarkHoverImageDeprecated = useAppSelector(
    rootSelectors.config.mapsPlacemarkHoverImageSelector,
  );
  const {
    image: placemarkImage,
    imageHover: placemarkHoverImage,
    imageActive: placemarkActiveImage,
    offset: placemarkOffset,
    offsetHover: placemarkHoverOffset,
    offsetActive: placemarkActiveOffset,
    size: placemarkSize,
    sizeHover: placemarkHoverSize,
    sizeActive: placemarkActiveSize,
  } = useAppSelector(rootSelectors.config.mapsPlacemark);
  const {
    enable: enableClusters,
    image: clusterImage,
    imageHover: clusterHoverImage,
    offset: clusterOffset,
    offsetHover: clusterHoverOffset,
    size: clusterSize,
    sizeHover: clusterHoverSize,
  } = useAppSelector(rootSelectors.config.mapsCluster);
  const [pinHovered, setPinHovered] = useState<string | null>(null);

  const isHidden = !dealerCenterItem;

  if (isHidden) {
    return null;
  }

  const { alternateName, name, address, contacts, openingHours } =
    dealerCenterItem?.data || {};
  const city = cityItem?.data.name || '';
  const fullAddress = getDealerAddress(address);
  const phones = getDealerPhones(contacts);
  const schedules = getDealerWorkTime(openingHours);

  if (!isDefined(address.latitude) || !isDefined(address.longitude)) {
    return null;
  }

  const pins = [
    {
      id: dealerCenterItem.id,
      latitude: Number(address.latitude),
      longitude: Number(address.longitude),
      classes: '',
    },
  ];

  const zoomControlPosition = {
    right: 18,
    top: mapRef?.current?.offsetHeight ? (mapRef.current.offsetHeight - 46) / 2 : 140,
  };

  const phone = {
    label: t('dealerMap.phone'),
    value: phones,
  };

  const schedule = {
    label: t('dealerMap.openingHours'),
    value: schedules.map(({ days, time }) => `${days}: ${time}`),
  };

  return (
    <section className={styles.dealerMap} ref={mapRef}>
      <Container className={styles.cardContainer}>
        <Row align="middle" className={styles.row}>
          <Col
            sm={0}
            md={12}
            lg={14}
            xl={12}
            xxl={10}
            ref={onBodyRefChange}
            className={styles.dealerCard}
          >
            <DealerCard>
              <Row gutter={[0, 'vertical-green']}>
                <Col span={24}>
                  <DealerCard.Location
                    name={alternateName || name}
                    city={city}
                    address={fullAddress}
                  />
                </Col>
                <Col span={24}>
                  <DealerCard.Contact phone={phone} schedule={schedule} />
                </Col>
              </Row>
            </DealerCard>
          </Col>
        </Row>
      </Container>
      <Row>
        <Col span={24} className={styles.mapWrapper}>
          <Map
            mapsProvider={mapsProvider}
            mapsKey={mapsKey}
            placemarkImage={placemarkImage || placemarkImageDeprecated}
            placemarkActiveImage={placemarkActiveImage || placemarkActiveImageDeprecated}
            placemarkHoverImage={placemarkHoverImage || placemarkHoverImageDeprecated}
            placemarkOffset={placemarkOffset}
            placemarkHoverOffset={placemarkHoverOffset}
            placemarkActiveOffset={placemarkActiveOffset}
            placemarkSize={placemarkSize}
            placemarkHoverSize={placemarkHoverSize}
            placemarkActiveSize={placemarkActiveSize}
            enableClustering={enableClusters}
            clusterImage={clusterImage}
            clusterHoverImage={clusterHoverImage}
            clusterOffset={clusterOffset}
            clusterHoverOffset={clusterHoverOffset}
            clusterSize={clusterSize}
            clusterHoverSize={clusterHoverSize}
            pins={pins}
            allPins={pins}
            selected={dealerCenterItem.id}
            zoomControlPosition={zoomControlPosition}
            hovered={pinHovered}
            occupiedAreas={occupiedArea}
          />
        </Col>
        <Col xs={24} sm={24} md={0}>
          <DealerCard className={styles.dealerMapInfo}>
            <Container>
              <DealerCard.Location
                name={alternateName || name}
                city={city}
                address={fullAddress}
                className={styles.dealerMapLocation}
              />
              <DealerCard.Contact
                phone={phone}
                schedule={schedule}
                className={styles.dealerMapContact}
              />
            </Container>
          </DealerCard>
        </Col>
      </Row>
    </section>
  );
}

export default DealerMap;
